// Cart.js
import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/apiConfig';

const SESSION_CART_KEY = 'ccc_guest_cart';
const SESSION_SIZE_KEY = 'ccc_guest_size';

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const parseAccessorySelections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getGuestCart = () => safeParse(sessionStorage.getItem(SESSION_CART_KEY), []);
const getGuestSize = () => safeParse(sessionStorage.getItem(SESSION_SIZE_KEY), null);

const setGuestCart = (items) => {
  try {
    sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_cart_updated'));
};

const Cart = () => {
  const navigate = useNavigate();

  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(() => safeParse(localStorage.getItem('user'), null));
  const [guestSize, setGuestSize] = useState(() => getGuestSize());

  const token = localStorage.getItem('token');
  const isLoggedIn = !!user?.userId && !!token;

  const normalizeCart = (arr) => {
    const items = Array.isArray(arr) ? arr : [];

    return items.map((x) => {
      // Server cart id normalization
      const cartId =
        x.cartId ??
        x.cartItemId ??
        x.cartItemID ??
        x.id ??
        null;

      // Image normalization
      const fabricImageUrl =
        x.fabricImageUrl ??
        x.fabricImageURL ??
        x.fabricImage ??
        x.fabricImagePath ??
        x.firstFabricImage ??
        x.imageUrl ??
        x.imageURL ??
        null;

      // Names
      const productName =
        x.productName ??
        x.ProductName ??
        '—';

      const fabricName =
        x.fabricName ??
        x.FabricName ??
        '';

      // Styles
      const styleOptionNames =
        Array.isArray(x.styleOptionNames) ? x.styleOptionNames :
        Array.isArray(x.styleOptions) ? x.styleOptions :
        typeof x.styleOptions === 'string'
          ? x.styleOptions.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
      const accessorySelections = parseAccessorySelections(
        x.accessorySelectionsJson ?? x.accessorySelections ?? x.AccessorySelectionsJson ?? null
      );

      // CreatedAt
      const createdAt =
        x.createdAt ??
        x.created_at ??
        x.addedOn ??
        x.added_on ??
        Date.now();

      // Price
      const price = Number(
        x.price ??
        x.totalPrice ??
        x.total_price ??
        0
      );

      // Guest ids
      const guestCartId = x.guestCartId ?? x.id ?? cartId ?? null;

      return {
        ...x,
        cartId,
        fabricImageUrl,
        productName,
        fabricName,
        styleOptionNames,
        accessorySelections,
        createdAt,
        price,
        guestCartId: guestCartId ?? `g-${Date.now()}-${Math.random()}`,
        _id: cartId ?? guestCartId ?? `g-${Date.now()}-${Math.random()}`
      };
    });
  };

  const loadCart = async () => {
    if (isLoggedIn) {
      try {
        const res = await fetch(`${API_BASE_URL}/UserManagement/GetCartByUserId/${user.userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('GetCartByUserId failed:', res.status, text);
          setCart([]);
          return;
        }

        const data = await res.json();
        setCart(normalizeCart(data));
      } catch (err) {
        console.error('Failed to load cart:', err);
        setCart([]);
      }
      return;
    }

    // guest
    const gs = getGuestSize();
    setGuestSize(gs);
    setCart(normalizeCart(getGuestCart()));
  };

  useEffect(() => {
    const syncUser = () => setUser(safeParse(localStorage.getItem('user'), null));
    const syncCart = () => loadCart();
    const syncGuestSize = () => setGuestSize(getGuestSize());

    window.addEventListener('storage', syncUser);
    window.addEventListener('ccc_user_updated', syncUser);

    window.addEventListener('ccc_cart_updated', syncCart);
    window.addEventListener('ccc_guest_size_updated', syncGuestSize);

    loadCart();

    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('ccc_user_updated', syncUser);
      window.removeEventListener('ccc_cart_updated', syncCart);
      window.removeEventListener('ccc_guest_size_updated', syncGuestSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user?.userId]);

  const deleteCartItem = async (id) => {
    if (isLoggedIn) {
      try {
        // Some backends use DELETE, some use POST. Try DELETE first, then fallback to POST.
        let res = await fetch(`${API_BASE_URL}/UserManagement/DeleteCartItem/${id}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          res = await fetch(`${API_BASE_URL}/UserManagement/DeleteCartItem/${id}`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
        }

        if (!res.ok) {
          const text = await res.text();
          console.error('DeleteCartItem failed:', res.status, text);
          return;
        }

        await loadCart();
        window.dispatchEvent(new Event('ccc_cart_updated'));
      } catch (err) {
        console.error('❌ Failed to delete cart item:', err);
      }
      return;
    }

    // guest
    const items = getGuestCart();
    const next = items.filter((x) => (x.guestCartId ?? x.id) !== id);
    setGuestCart(next);
    setCart(normalizeCart(next));
  };

  const hasItems = cart.length > 0;

  const orderNow = () => {
    navigate('/checkout');
  };

  const pickGuestSizeFromItemOrSession = (item) => {
    // If you stored a snapshot per item (recommended), prefer it:
    const snap =
      item.sizeSnapshot ??
      item.guestSizeSnapshot ??
      item.sessionSize ??
      null;

    if (snap && typeof snap === 'object') return snap;

    // Otherwise fallback to global session guest size (ccc_guest_size):
    return guestSize;
  };

  const renderSizeLabel = (item) => {
    const fmt = (val) => {
      if (val === null || val === undefined || val === '') return null;
      const num = Number(val);
      return Number.isFinite(num) ? `${num}` : String(val);
    };

    const bodyFromItem = item.bodyMeasurement || item.BodyMeasurement || null;
    const shirtFromItem = item.shirtMeasurement || item.ShirtMeasurement || null;

    const bodyDetails = (m) => {
      if (!m) return null;
      const parts = [
        ['Neck', fmt(m.neckAround ?? m.NeckAround)],
        ['Sleeve', fmt(m.sleeveLength ?? m.SleeveLength)],
        ['Shoulder', fmt(m.shoulderWidth ?? m.ShoulderWidth)],
        ['Chest', fmt(m.chestAround ?? m.ChestAround)],
        ['Waist/Stomach', fmt(m.waistAround ?? m.WaistAround)],
        ['Length', fmt(m.shirtLength ?? m.ShirtLength)],
        ['Biceps', fmt(m.bicepAround ?? m.BicepAround)],
        ['Cuffs/Wrist', fmt(m.wristAround ?? m.WristAround)],
        ['Fit', (m.fit ?? m.Fit ?? '').toString().trim() || null]
      ].filter(([, v]) => v !== null);
      return parts.length ? `Body Measurement: ${parts.map(([k, v]) => `${k} ${v}`).join(', ')}` : 'Body Measurement';
    };

    const shirtDetails = (m) => {
      if (!m) return null;
      const parts = [
        ['Collar/Neck', fmt(m.collarAround ?? m.CollarAround)],
        ['Sleeve', fmt(m.sleeveLength ?? m.SleeveLength)],
        ['Shoulder/Yoke', fmt(m.yokeWidth ?? m.YokeWidth)],
        ['Chest', fmt(m.chestWidth ?? m.ChestWidth)],
        ['Stomach', fmt(m.midsectionWidth ?? m.MidsectionWidth)],
        ['Length', fmt(m.shirtLength ?? m.ShirtLength)],
        ['Biceps', fmt(m.sleeveWidth ?? m.SleeveWidth)],
        ['Cuffs', fmt(m.cuffAround ?? m.CuffAround)]
      ].filter(([, v]) => v !== null);
      return parts.length ? `Shirt Measurement: ${parts.map(([k, v]) => `${k} ${v}`).join(', ')}` : 'Shirt Measurement';
    };

    // Logged-in cart fields from server
    if (item.smartSizeName) return `Smart Size: ${item.smartSizeName}`;
    if (item.bodyMeasurementLabel) return bodyDetails(bodyFromItem) || 'Body Measurement';
    if (item.shirtMeasurementLabel) return shirtDetails(shirtFromItem) || 'Shirt Measurement';
    if (item.isMailUsSelected) return 'Mail Us a Shirt';

    const standardCollar = fmt(item.standardCollarSize ?? item.standardCollar);
    const standardSleeve = fmt(item.standardSleeveLength ?? item.standardSleeve);
    const standardFit = (item.standardFit ?? '').toString().trim();
    if (standardCollar || standardSleeve || standardFit) {
      const parts = [];
      if (standardCollar) parts.push(`Collar ${standardCollar}`);
      if (standardSleeve) parts.push(`Sleeve ${standardSleeve}`);
      if (standardFit) parts.push(standardFit);
      return `Standard Size: ${parts.join(', ')}`;
    }

    // Guest: prefer item sizeType if present
    const itemSizeType =
      item.sizeType ||
      item.selectedSizeType ||
      item.sizeSelectionType ||
      item.size_mode ||
      '';

    const st = String(itemSizeType).toLowerCase();

    // If standard text exists on the item
    const standardFromItem =
      item.standardSize ||
      item.standardSizeName ||
      item.selectedStandardSize ||
      item.size ||
      item.sizeName;

    if (standardFromItem && (st.includes('standard') || !st)) return `Standard Size: ${standardFromItem}`;
    if (st.includes('smart')) return 'Smart Size';
    if (st.includes('body')) {
      const snapshot = pickGuestSizeFromItemOrSession(item);
      return bodyDetails(snapshot?.body || snapshot?.bodyMeasurement || bodyFromItem) || 'Body Measurement';
    }
    if (st.includes('shirt')) {
      const snapshot = pickGuestSizeFromItemOrSession(item);
      return shirtDetails(snapshot?.shirt || snapshot?.shirtMeasurement || shirtFromItem) || 'Shirt Measurement';
    }
    if (st.includes('mail')) return 'Mail Us a Shirt';

    // Guest: derive from snapshot/session
    const gs = pickGuestSizeFromItemOrSession(item);
    if (!gs) return '—';

    const gsType = String(gs.sizeType || '').toLowerCase();

    if (gsType.includes('smart')) return `Smart Size: ${gs.smartSizeName || gs.smartSize || 'Selected'}`;
    if (gsType.includes('body')) return bodyDetails(gs.body || gs.bodyMeasurement || null) || 'Body Measurement';
    if (gsType.includes('shirt')) return shirtDetails(gs.shirt || gs.shirtMeasurement || null) || 'Shirt Measurement';
    if (gsType.includes('mail')) return 'Mail Us a Shirt';

    if (gsType.includes('standard') || !gsType) {
      const s = gs.standard || {};
      const collar = s.collarSize ?? s.collar ?? '';
      const sleeve = s.sleeveLength ?? s.sleeve ?? '';
      const fit = s.fit ?? '';
      const parts = [];
      if (collar) parts.push(`Collar ${collar}`);
      if (sleeve) parts.push(`Sleeve ${sleeve}`);
      if (fit) parts.push(fit);
      return parts.length ? `Standard Size: ${parts.join(', ')}` : 'Standard Size';
    }

    return '—';
  };

  const renderAccessorySelectionLabel = (s) => {
    const left = s?.label || s?.key || 'Option';
    const right = s?.optionLabel || s?.value || '';
    return right ? `${left}: ${right}` : left;
  };

  return (
    <>
      <Header />
      <main className="rbt-main-wrapper">
        <div className="rbt-breadcrumb-default ptb--100 ptb_md--50 ptb_sm--30 bg-gradient-1">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="breadcrumb-inner text-center">
                  <h2 className="title">My Cart</h2>
                  <ul className="page-list">
                    <li className="rbt-breadcrumb-item"><a href="/">Home</a></li>
                    <li><div className="icon-right"><i className="feather-chevron-right"></i></div></li>
                    <li className="rbt-breadcrumb-item active">Cart</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rbt-my-account-area rbt-section-gap" style={{ width: '100%' }}>
          <div className="container" style={{ width: '100%' }}>
            {hasItems && (
              <div className="mt-4">
                <button
                  className="rbt-btn btn-gradient"
                  style={{ padding: '12px', fontSize: '16px' }}
                  onClick={orderNow}
                >
                  Order Now
                </button>
              </div>
            )}

            <br />

            <div className="row g-5" style={{ width: '100%' }}>
              <div className="col-lg-12 col-12" style={{ width: '100%' }}>
                <div className="rbt-my-account-inner" style={{ width: '100%' }}>
                  <div className="rbt-my-account-table table-responsive text-center" style={{ width: '100%' }}>
                    <table className="table table-bordered" style={{ width: '100%' }}>
                      <thead className="thead-light">
                        <tr>
                          <th>Image</th>
                          <th>Name</th>
                          <th>Size</th>
                          <th>Monogram</th>
                          <th>Added On</th>
                          <th>Price</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {!hasItems ? (
                          <tr>
                            <td colSpan="7">No items in cart.</td>
                          </tr>
                        ) : (
                          cart.map((item) => (
                            <tr key={item._id}>
                              <td>
                                <img
                                  src={item.fabricImageUrl || "/assets/images/default.jpg"}
                                  alt={item.productName || "Product"}
                                  style={{ width: "60px", height: "60px", objectFit: "cover" }}
                                />
                              </td>

                              <td>
                                <strong>{item.productName}</strong>
                                <br />
                                <small style={{ color: '#666' }}>{item.fabricName}</small>
                                <br />
                                <small style={{ color: '#888', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {(item.styleOptionNames || []).join(', ')}
                                </small>
                                {!!item.accessorySelections?.length && (
                                  <>
                                    <br />
                                    <small style={{ color: '#666', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                      {(item.accessorySelections || []).map(renderAccessorySelectionLabel).join(', ')}
                                    </small>
                                  </>
                                )}
                              </td>

                              <td>{renderSizeLabel(item)}</td>

                              <td>
                                {item.isMonogram ? (
                                  <>
                                    <div>{item.monogramInitials}</div>
                                    <div style={{ fontSize: '0.8em', color: '#888' }}>
                                      {item.monogramStyle}, {item.monogramColor}, {item.monogramPlacement}
                                    </div>
                                  </>
                                ) : (
                                  '—'
                                )}
                              </td>

                              <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                              <td>${Number(item.price || 0).toFixed(2)}</td>

                              <td>
                                <button
                                  className="rbt-btn btn-gradient btn-sm"
                                  onClick={() => deleteCartItem(isLoggedIn ? item.cartId : (item.guestCartId ?? item.id))}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {!isLoggedIn && hasItems && (
                    <div className="mt-3" style={{ color: '#666' }}>
                      You’re viewing a guest cart (saved in this browser).
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Cart;
